import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, AlertCircle, Check, Eye, EyeOff } from "lucide-react";
import AuthShell from "@/pages/auth/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [packs, setPacks] = useState([]);
  const [packSlug, setPackSlug] = useState("b2b-sales");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("");

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/crm/packs")
      .then(({ data }) => setPacks(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await signup({
      name: name.trim(),
      email: email.trim(),
      password,
      workspace_name: workspaceName.trim() || undefined,
      pack_slug: packSlug,
    });
    setSubmitting(false);
    if (res.ok) navigate("/app/crm", { replace: true });
    else setError(res.error);
  };

  return (
    <AuthShell
      title={
        <>
          Start your <span className="italic">CRM.</span>
        </>
      }
      subtitle="Free to use. Pick an industry pack and you'll be in your dashboard in under a minute."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="link-to-login"
            className="text-[#1A1A1A] underline underline-offset-4 hover:no-underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} data-testid="signup-form" className="space-y-1.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          <div>
            <label className="caption block mb-1">Your name</label>
            <input
              data-testid="signup-name"
              required
              className="editorial-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="caption block mb-1">Email</label>
            <input
              data-testid="signup-email"
              type="email"
              required
              autoComplete="email"
              className="editorial-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={submitting}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          <div>
            <label className="caption block mb-1">Password</label>
            <div className="relative">
              <input
                data-testid="signup-password"
                type={showPw ? "text" : "password"}
                required
                minLength={6}
                autoComplete="new-password"
                className="editorial-input pr-8"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#6B6A68] hover:text-[#1A1A1A]"
                aria-label="Toggle password visibility"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="caption block mb-1">Workspace name (optional)</label>
            <input
              data-testid="signup-workspace"
              className="editorial-input"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Acme Sales"
              disabled={submitting}
            />
          </div>
        </div>

        {/* Industry pack picker */}
        <div className="mt-6">
          <label className="caption block mb-3">Pick an industry pack</label>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5">
            {packs.map((p) => {
              const active = p.slug === packSlug;
              return (
                <button
                  type="button"
                  key={p.slug}
                  data-testid={`pack-option-${p.slug}`}
                  onClick={() => setPackSlug(p.slug)}
                  className={`relative text-left p-3 rounded-xl border transition-all ${
                    active
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFCFB]"
                      : "border-[#E8E6E1] bg-[#FDFCFB] text-[#1A1A1A] hover:border-[#1A1A1A]/40"
                  }`}
                >
                  {active && (
                    <Check className="absolute top-2.5 right-2.5 w-3.5 h-3.5" />
                  )}
                  <div className="font-serif-display text-[15px] tracking-tight leading-tight pr-5">
                    {p.name}
                  </div>
                  <div
                    className={`mt-1 text-[11px] leading-snug ${
                      active ? "text-[#FDFCFB]/65" : "text-[#6B6A68]"
                    }`}
                  >
                    {p.tagline}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div
            data-testid="signup-error"
            className="mt-6 flex items-start gap-2 text-sm text-[#B03A2E]"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          data-testid="signup-submit"
          type="submit"
          disabled={submitting}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm font-medium hover:bg-[#333] transition-all disabled:opacity-60"
        >
          {submitting ? "Creating workspace…" : "Create my CRM"}
          <ArrowUpRight className="w-4 h-4" />
        </button>

        <p className="mt-5 text-xs text-[#6B6A68] leading-relaxed">
          By signing up you agree to our{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-[#1A1A1A]">
            Terms
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-[#1A1A1A]">
            Privacy Policy
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}
